/**
 * @module builder
 */
import type { OptionsDocument, PartnerContentFeatures, PresentationSectionV2Option } from 'paintscout'
import { getPrefabSections } from '../../../../database/fixtures/presentations/prefabSections'
import { getFeature } from '../../get-features'
import { ADDABLE_SHERWIN_WILLIAMS_PAGES } from '../sherwin-williams-section-types'
import { uuid } from '../../../util'

/**
 * Sherwin-Williams partner content (#21311), offered as whole **sections** — the bigger unit — not
 * as individual blocks. Each section wraps a single static partner block (the v1 components, reused
 * by v2 — see react-quote PresentationNext/sections/partners/).
 *
 * These were "page options" before #21990; only the level's name changed.
 */
export function getSherwinWilliamsSectionOptions(): PresentationSectionV2Option[] {
  return ADDABLE_SHERWIN_WILLIAMS_PAGES.map(({ type, label }) => ({
    type,
    label,
    title: label,
    active: true,
    categories: ['sherwin-williams'],
    advanced: {
      useForNav: true,
      allowCustom: false
    },
    blocks: [
      {
        key: uuid(),
        type,
        label,
        title: label,
        active: true,
        advanced: {
          useForNav: true,
          allowCustom: false
        }
      }
    ]
  }))
}

/**
 * The v2 section catalog — what the "Add Section" dialog offers: prefab bundles of blocks, plus the
 * feature-gated Sherwin-Williams partner sections.
 *
 * This is the v2 counterpart of v1's getPresentationPageOptions (which used to serve both versions
 * via an `isNewPresentation` flag). Unlike the #21312 v1-parity blocks, the partner sections are
 * feature-gated only — deliberately NOT superadmin-gated.
 */
export function getPresentationNextSectionOptions(args: {
  options?: OptionsDocument
  type?: string
}): PresentationSectionV2Option[] {
  const { options, type } = args

  let sectionOptions = getPrefabSections()

  const partnerContent: PartnerContentFeatures = getFeature({ options, path: 'partnerContent' })
  if (partnerContent?.enabled && partnerContent?.sherwinWilliams?.presentationPages?.enabled) {
    sectionOptions = [...sectionOptions, ...getSherwinWilliamsSectionOptions()]
  }

  if (type) {
    return sectionOptions.filter((option) => option.type === type)
  }

  return sectionOptions
}
